/** The rail: Rooms and Direct as two tabs, one search for everything, each room with its channels and circles. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { HostTalkResponse } from "../../shared/talk";
import { Icon } from "../components/ui/Icon";
import { PersonRow } from "./parts";
import { directPath, type Place, roomOfPlace, searchPath } from "./places";
import { AllRooms, RoomSections, useKeepRoom } from "./RailRooms";
import { firstRoom, railSections } from "./rooms";
import { listTime } from "./when";

export type RailTab = "rooms" | "direct";
type Filter = "all" | "unread" | "email";

/** The search box, kept as typed and read once the person pauses. */
function RailSearch({ q, wide }: { q: string; wide: boolean }) {
  const navigate = useNavigate();
  const [value, setValue] = useState(q);
  useEffect(() => setValue(q), [q]);
  useEffect(() => {
    const word = value.trim();
    if (word === q || word.length < 2) return;
    const t = window.setTimeout(() => navigate(searchPath(word), { replace: q !== "" }), 350);
    return () => window.clearTimeout(t);
  }, [value, q, navigate]);
  return (
    <label className={q ? "rail-q on" : "rail-q"}>
      <Icon name="search" />
      <input
        aria-label="Search"
        placeholder={wide ? "Search messages, files, people" : "Search"}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      {value ? (
        <button type="button" className="rail-qx" aria-label="Clear the search" onClick={() => setValue("")}>
          <Icon name="x" />
        </button>
      ) : null}
    </label>
  );
}

export function Rail({
  rail,
  place,
  tab,
  onTab,
  q,
  wide,
  onCircle,
  reload,
}: {
  rail: HostTalkResponse | null;
  place: Place;
  tab: RailTab;
  onTab: (t: RailTab) => void;
  q: string;
  wide: boolean;
  onCircle: (webinarId: string) => void;
  reload: () => void;
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [all, setAll] = useState(false);
  const aside = useRef<HTMLElement>(null);
  // Why: All rooms is a different list in the same pane, so it starts at its top.
  useEffect(() => void aside.current?.scrollTo({ top: 0 }), [all]);
  const keep = useKeepRoom(reload);
  const sections = useMemo(() => railSections(rail?.rooms ?? []), [rail]);
  // Why a first room when none is open: the Rooms tab always shows one room's places, as it did before sections.
  const open = roomOfPlace(place, rail) ?? firstRoom(rail?.rooms ?? []);
  const direct = (rail?.direct ?? []).filter((r) => (filter === "unread" ? r.unread : filter === "email" ? r.hasEmail : true));

  return (
    <aside className="rail" ref={aside}>
      <div className="rail-h">
        <h1>Conversations</h1>
      </div>
      <div className="rtabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "rooms"} className={tab === "rooms" ? "on" : ""} onClick={() => onTab("rooms")}>
          Rooms {rail?.roomsNew ? <i>{rail.roomsNew}</i> : null}
        </button>
        <button type="button" role="tab" aria-selected={tab === "direct"} className={tab === "direct" ? "on" : ""} onClick={() => onTab("direct")}>
          Direct {rail?.directUnread ? <i>{rail.directUnread}</i> : null}
        </button>
      </div>
      <RailSearch q={q} wide={wide} />
      {tab === "rooms" && all ? (
        <AllRooms rooms={rail?.rooms ?? []} onBack={() => setAll(false)} />
      ) : tab === "rooms" ? (
        <RoomSections kept={sections.kept} active={sections.active} total={rail?.rooms.length ?? 0} open={open} place={place} onAll={() => setAll(true)} onKeep={(r) => void keep(r)} onCircle={onCircle} />
      ) : (
        <div className="rail-s">
          <div className="rail-f" role="tablist" aria-label="Show">
            <button type="button" className={filter === "all" ? "on" : undefined} onClick={() => setFilter("all")}>
              All
            </button>
            <button type="button" className={filter === "unread" ? "on" : undefined} onClick={() => setFilter("unread")}>
              Unread{rail?.directUnread ? ` ${rail.directUnread}` : ""}
            </button>
            <button type="button" className={filter === "email" ? "on" : undefined} onClick={() => setFilter("email")}>
              Email
            </button>
          </div>
          {direct.map((r) => (
            <PersonRow key={r.contactId} row={r} line={r.last} when={listTime(r.lastAt)} on={place.kind === "direct" && place.contactId === r.contactId} onClick={() => navigate(directPath(r.contactId))} />
          ))}
          {rail && direct.length === 0 ? <p className="empty-note">Nobody here yet.</p> : null}
        </div>
      )}
    </aside>
  );
}
